import { Badge, Tag } from '@vrplatform/components';
import { useDashboard, useQuery, useTeam } from '@vrplatform/data';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { day, formatCurrency, hasValue, sortBy } from '@vrplatform/utils';
import {
  Anchor,
  Group,
  HoverCard,
  ScrollArea,
  Skeleton,
  Stack,
  rem,
  useMantineTheme,
} from '@mantine/core';
import {
  formatOwnerName,
  formatOwnershipBusinessModel,
  formatPercentage,
  pluralize,
} from '@vrplatform/ui-common';
import { useCallback, useMemo } from 'react';
import { OwnerBadge } from '../../../../components';
import { getListingDeactivation } from '../../detail/useListingDetailViewQuery';
import { useListingsTableFilter } from './ListingsTableFilter';
import { ListingsTableNameCell } from './ListingsTableNameCell';
import type { ListingRow } from './useListingsTableQuery';
import { useNavigate } from 'react-router';
import type { recurringFeeType_enum } from '@vrplatform/graphql';
import {
  getFeeTitleFromFeeTypeRoute,
  getFeeTypeRouteFromRecurringFeeEnum,
} from '../../../fees/overview/_utils';
import { Text } from '@mantine/core';

function useGotoFeesOverview(listingId: string, type: recurringFeeType_enum) {
  const goto = useNavigate();

  return useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement | HTMLDivElement, MouseEvent>
    ) => {
      event.stopPropagation();
      goto(
        `/listing/${listingId}/fees-commissions/${getFeeTypeRouteFromRecurringFeeEnum(type)}`
      );
    },
    [goto, listingId, type]
  );
}

type TableColumn = MRT_ColumnDef<ListingRow>;

export const useListingsTableColumns = () => {
  const [dashboard] = useDashboard();
  const isOwnerPortal = dashboard === 'owner';

  const { filter } = useListingsTableFilter();

  const status = filter.status ?? 'active';

  return useMemo(() => {
    const nameColumn: TableColumn = {
      accessorKey: 'name',
      enableColumnDragging: false,
      header: 'Listing',
      minSize: 300,
      Cell: ({ row }) => {
        return (
          <ListingsTableNameCell
            address={row.original.address}
            name={row.original.name}
            connectionId={row.original.connectionId}
          />
        );
      },
    };

    const businessModel: TableColumn = {
      enableColumnDragging: false,
      header: 'Business Model',
      size: 140,
      Cell: ({ row }) => {
        if (!row.original.activeOwnership?.businessModel) return '-';

        return (
          <Tag>
            {formatOwnershipBusinessModel(
              row.original.activeOwnership?.businessModel
            )}
          </Tag>
        );
      },
    };

    // const collectionColumn: TableColumn =  {
    //         id: 'collectionId',
    //         header: 'Listing Group',
    //         minSize: 200,
    //         enableColumnDragging: false,
    //         getGroupingValue: (row) => row.collection?.id || '',
    //         enableGrouping: true,
    //         GroupedCell: ({ row }) => row.original.collection?.name,
    //         maxSize: 70,
    //         Cell: ({ row }) => {
    //           const collection = row.original.collection;
    //           const listingId = row.original.id;

    //           if (!listingId) return null;

    //           return (
    //             collection?.id && (
    //               <ListingCollectionSelect
    //                 collection={collection}
    //                 listingId={listingId}
    //               />
    //             )
    //           );
    //         },
    //         enableHiding: false,
    //       }

    const ownershipColumn: TableColumn = {
      accessorKey: 'ownerships',
      header: 'Current Ownership',
      enableSorting: false,
      enableColumnDragging: false,
      size: 140,
      Cell: ({ row }) => {
        const [{ id: teamId }] = useTeam();
        const members = row.original.activeOwnership?.members || [];
        const contactIds = members.map((m) => m.contactId).filter(hasValue);

        const { data, isLoading, error } = useQuery(
          (q, args) => {
            const deactivation =
              args.status === 'active'
                ? undefined
                : getListingDeactivation(
                    q.listing({ id: args.listingId }),
                    args.today
                  );

            const contacts =
              args.status === 'inactive' || !args.contactIds.length
                ? []
                : q
                    .contacts({
                      where: {
                        id: {
                          _in: args.contactIds,
                        },
                      },
                      order_by: [{ name: 'asc_nulls_last' }],
                    })
                    .map((c) => ({
                      id: c.id,
                      name: formatOwnerName(c, {
                        lastNameFirst: false,
                      }),
                      type: c.companyType
                        ? ('company' as const)
                        : ('individual' as const),
                    }));

            return {
              contacts,
              deactivation,
            };
          },
          {
            skip: !row.original.id,
            queryKey: ['owners'],
            variables: {
              contactIds,
              teamId,
              status,
              listingId: row.original.id,
              today: day().yyyymmdd(),
            },
            keepPreviousData: true,
          }
        );

        if (error)
          return <Tag color="red">{error?.message || 'Failed to load'}</Tag>;

        if (isLoading) return <Skeleton height={20} width={150} />;

        if (status === 'inactive') {
          if (!data?.deactivation || data.deactivation.status === 'active')
            return '-';

          return (
            <Badge color="yellow">
              Deactivated from{' '}
              {day(data.deactivation.period.startAt).format('ll')}
              {data.deactivation.period.endAt
                ? ` until ${day(data.deactivation.period.endAt).format('ll')}`
                : ''}
            </Badge>
          );
        }

        if (!row.original.activeOwnership) return '-';

        return (
          <Group gap={rem(5)}>
            {members.map((member) => {
              const contact = data?.contacts?.find(
                (c) => c.id === member.contactId
              );

              if (!contact) return null;

              return (
                <OwnerBadge
                  key={contact.id}
                  ownerId={contact.id}
                  type={contact.type}
                  name={contact.name}
                  splitSuperBasePoints={member.split ?? 0}
                />
              );
            })}
          </Group>
        );
      },
    };

    const mgtComColumns: TableColumn = {
      header: getFeeTitleFromFeeTypeRoute('commissions'),
      enableSorting: false,
      enableColumnDragging: false,
      size: 80,
      Cell: ({ row }) => {
        const gotoOverview = useGotoFeesOverview(
          row.original.id,
          'managementFee'
        );

        const fees = row.original.activeRecurringFeePeriods?.filter(
          (x) => x.recurringFee.type === 'managementFee'
        );

        if (!fees?.length) {
          return (
            <Anchor component="button" size="xs" onClick={gotoOverview}>
              Add rate
            </Anchor>
          );
        }

        if (fees.length === 1) {
          return (
            <Anchor
              component="button"
              size="xs"
              onClick={gotoOverview}
              c="dark"
            >
              {formatPercentage(fees[0].rate ?? 0, 'super-base-points')}
            </Anchor>
          );
        }

        return (
          <FeesDropdown
            listingId={row.original.id}
            type="managementFee"
            fees={fees}
          />
        );
      },
    };

    const getFeesColumn = (type: recurringFeeType_enum): TableColumn => {
      const route = getFeeTypeRouteFromRecurringFeeEnum(type);
      const title = getFeeTitleFromFeeTypeRoute(route);

      return {
        id: type,
        header: pluralize(2, title),
        enableSorting: false,
        enableColumnDragging: false,
        size: 80,
        Cell: ({ row }) => {
          const gotoOverview = useGotoFeesOverview(row.original.id, type);
          const fees = row.original.activeRecurringFeePeriods?.filter(
            (x) => x.recurringFee.type === type
          );

          if (fees?.length) {
            return (
              <FeesDropdown
                listingId={row.original.id}
                type={type}
                fees={fees}
              />
            );
          }

          return (
            <Anchor size="xs" component="button" onClick={gotoOverview}>
              Add fees
            </Anchor>
          );
        },
      };
    };

    if (isOwnerPortal) {
      return [nameColumn];
    }

    return [
      nameColumn,
      ownershipColumn,
      businessModel,
      mgtComColumns,
      getFeesColumn('bookingChannelFee'),
      getFeesColumn('merchantFee'),
      getFeesColumn('cleaningFee'),
      getFeesColumn('additionalFee'),
    ];
  }, [isOwnerPortal, status]);
};

const FeesDropdown = ({
  listingId,
  type,
  fees,
}: {
  listingId: string;
  type: recurringFeeType_enum;
  fees: NonNullable<ListingRow['activeRecurringFeePeriods']>;
}) => {
  const [{ currency }] = useTeam();
  const theme = useMantineTheme();
  const gotoOverview = useGotoFeesOverview(listingId, type);

  return (
    <HoverCard>
      <HoverCard.Target>
        <Tag
          component="button"
          color={theme.colors[theme.primaryColor][6]}
          radius={theme.radius.xl}
          size="xs"
          onClick={gotoOverview}
        >
          {fees.length} {pluralize(fees.length, 'fee')}
        </Tag>
      </HoverCard.Target>
      <HoverCard.Dropdown
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <ScrollArea.Autosize mah={300}>
          <Stack>
            {sortBy(fees, (x) => x.recurringFee.name || '-').map((fee) => (
              <Group justify="space-between" key={fee.id} wrap="nowrap">
                <Text>{fee.recurringFee.name}</Text>
                <Text c="gray" ta="right">
                  {fee.recurringFee.rateType === 'flat'
                    ? formatCurrency((fee.rate ?? 0) / 100, currency)
                    : formatPercentage(fee.rate ?? 0, 'super-base-points')}
                </Text>
              </Group>
            ))}
          </Stack>
        </ScrollArea.Autosize>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};
